import { makeObservable, observable, action, runInAction, toJS } from 'mobx';
import StoreUtils from './store-utils';
import UserApi from '../rest-api/user-api';
import FuseWrapper from './fuse-wrapper';

const makeDexieSafe = (value) => JSON.parse(JSON.stringify(toJS(value)));

class UserCreatedHandoutStore {
    userCreatedHandoutData = {};
    db;
    idToken;
    fuseWrapper;
    isInitialized = false;
    isDisposed = false;

    constructor(userId, idToken) {
        this.db = StoreUtils.createDexieUserDB(userId, 'UserCreatedHandoutStore');
        this.idToken = idToken;

        this.db.version(1).stores({
            userCreatedHandouts:
                'handoutId, contentId, contentStatus, title, contentName, alphabeticalName, lastUpdated',
        });

        makeObservable(this, {
            userCreatedHandoutData: observable,
            setUserCreatedHandoutData: action,
            addOrUpdateUserCreatedHandout: action,
            isInitialized: observable,
        });
    }

    normalizeHandout(handout) {
        const contentId = handout.contentId || handout.handoutId;
        const rawStatus = handout.contentStatus || handout.status || handout.handoutStatus;
        return {
            ...handout,
            handoutId: handout.handoutId || contentId,
            contentId,
            contentName: handout.contentName || handout.title || 'Untitled handout',
            alphabeticalName: handout.alphabeticalName || handout.title || handout.contentName || 'Untitled handout',
            contentType: 'userCreatedHandout',
            contentStatus: rawStatus === 'published' ? 'published' : 'unpublished',
        };
    }

    rebuildFuseWrapper() {
        this.fuseWrapper = new FuseWrapper(this.userCreatedHandoutData);
    }

    async initialize() {
        if (this.isDisposed) return;

        const storedHandouts = await this.db.userCreatedHandouts.toArray();

        if (this.isDisposed) return;

        runInAction(() => {
            storedHandouts.forEach((handout) => {
                const normalized = this.normalizeHandout(handout);
                this.userCreatedHandoutData[normalized.handoutId] = normalized;
            });
            this.rebuildFuseWrapper();
        });

        try {
            await this.fetchUserCreatedHandoutsFromBackend();
        } catch (error) {
            console.error('UserCreatedHandoutStore initialization fetch error:', error);
        }

        runInAction(() => {
            this.isInitialized = true;
        });
    }

    async fetchUserCreatedHandoutsFromBackend() {
        const result = await UserApi.getUserCreatedHandouts(this.idToken);
        if (this.isDisposed) return;

        const handouts = result?.userCreatedHandouts || result?.handouts;
        if (Array.isArray(handouts)) {
            await this.setUserCreatedHandoutData(handouts);
        }
    }

    async setUserCreatedHandoutData(handouts) {
        if (this.isDisposed || !Array.isArray(handouts)) return;

        const safeHandouts = handouts.map((handout) => makeDexieSafe(this.normalizeHandout(handout)));
        try {
            await this.db.userCreatedHandouts.clear();
            if (safeHandouts.length > 0) {
                await this.db.userCreatedHandouts.bulkPut(safeHandouts);
            }
        } catch (error) {
            if (this.isDisposed || error?.name === 'DatabaseClosedError') {
                console.warn('UserCreatedHandoutStore skipped cache update because the database is closed.');
                return;
            }
            throw error;
        }

        if (this.isDisposed) return;

        runInAction(() => {
            this.userCreatedHandoutData = {};
            safeHandouts.forEach((handout) => {
                this.userCreatedHandoutData[handout.handoutId] = handout;
            });
            this.rebuildFuseWrapper();
        });
    }

    async addOrUpdateUserCreatedHandout(handout) {
        if (this.isDisposed) return;

        if (!handout?.handoutId && !handout?.contentId) {
            console.error('Invalid user-created handout data:', handout);
            return;
        }

        const safeHandout = makeDexieSafe(this.normalizeHandout(handout));
        try {
            await this.db.userCreatedHandouts.put(safeHandout);
        } catch (error) {
            if (this.isDisposed || error?.name === 'DatabaseClosedError') {
                console.warn('UserCreatedHandoutStore skipped cache write because the database is closed.');
                return;
            }
            throw error;
        }

        if (this.isDisposed) return;

        runInAction(() => {
            this.userCreatedHandoutData = {
                ...this.userCreatedHandoutData,
                [safeHandout.handoutId]: safeHandout,
            };
            this.rebuildFuseWrapper();
        });
    }

    async getUserCreatedHandoutById(handoutId) {
        if (this.userCreatedHandoutData[handoutId]) {
            return this.userCreatedHandoutData[handoutId];
        }
        if (this.isDisposed) return null;
        return this.db.userCreatedHandouts.get(handoutId);
    }

    getPublishedHandouts() {
        return Object.values(this.userCreatedHandoutData).filter((handout) => handout.contentStatus === 'published');
    }

    searchContentMeta(searchTerm) {
        if (this.isInitialized && this.fuseWrapper) {
            return this.fuseWrapper.searchContentMeta(searchTerm).map((meta) => ({
                ...meta,
                handoutId: meta.handoutId || meta.contentId,
                contentType: 'userCreatedHandout',
                contentStatus: 'published',
            }));
        }
        return [];
    }

    async deleteDatabase() {
        this.isDisposed = true;
        await this.db.delete();
    }
}

export default async function createUserCreatedHandoutStore(userId, idToken) {
    const store = new UserCreatedHandoutStore(userId, idToken);
    await store.initialize();
    return store;
}
